import * as React from 'react';
import Income from "../../../model/Income";
import {FormLabel, Grid, Typography, useTheme} from "@mui/material";
import {useEffect, useState} from "react";
import {
  dateToLocalFormat,
  numberToMoneyFormat,
  numberToPercentFormat
} from "../../../stores/StoreUtilities";
import Model from "../../../model/Model";
import IncomeEditDialog from "./IncomeEditDialog";
import {IncomeIcon} from "../../../styles/Icons";
import ModelCard2 from "../../../components/model/ModelCard2";
import {isNumber} from "../../../model/ModelUtilities";
import Snapshot from "../../../model/Snapshot";
import PaycheckEditDialog from "./PaycheckEditDialog";
import {useResources} from "../../../stores/ResourceProvider";
import {isManagedRiderIncome} from "../../../components/calculator/AnnuityRider";

const IncomeCard = ({
  income,
  model,
  snapshot,
  projected,
  expanded,
  onChange,
  onSave,
  onDelete,
  onArchive,
  dragHandleProps
}: {
  income: Income
  model: Model
  snapshot?: Snapshot
  projected?: number
  expanded?: boolean
  onChange?(event: any, expanded: boolean): void
  onSave?(income: Income): void
  onDelete?(income: Income): void
  onArchive?(income: Income): void
  dragHandleProps?: any
}) => {
  const [incomeModel, setIncomeModel] = useState<Income>(income)
  const [label, setLabel] = useState<string | undefined>()
  const [description, setDescription] = useState<string>("")
  const [amount, setAmount] = useState<string | undefined>()
  const [amountDate, setAmountDate] = useState<string | undefined>()
  const [projectedAmount, setProjectedAmount] = useState<string | undefined>()
  const [incomeType, setIncomeType] = useState<string | undefined>()
  const [owner, setOwner] = useState<string | undefined>()
  const [schedule, setSchedule] = useState<string | undefined>()
  const [startDate, setStartDate] = useState<string | undefined>()
  const [endDate, setEndDate] = useState<string | undefined>()
  const [annualInf, setAnnualInf] = useState<string | undefined>()
  const [annualInfLock, setAnnualInfLock] = useState<boolean>(false)
  const [survivorPercent, setSurvivorPercent] = useState<string | undefined>()
  const [archived, setArchived] = useState<boolean>(false)
  const [editOpen, setEditOpen] = useState<boolean>(false)
  const [saved, setSaved] = useState<boolean>(false)
  const [isLocked, setIsLocked] = useState<boolean>(false)

  useEffect(() => {
    init(income)
  }, [income, projected])

  const init = (income: Income) => {
    setIncomeModel(income)
    setDescription(income.description)
    // const itemAmount = modelStore.getBudgetItemAmountDetail(income, model, snapshot)
    // setAmount(numberToMoneyFormat(itemAmount.amount, 0))
    // setAmountDate((dateToLocalFormat(itemAmount.amountDate)))
    setAmount(numberToMoneyFormat(income.amount, Number.isInteger(income.amount) ? 0 : 2))
    setAmountDate(dateToLocalFormat(income.amountDate))
    setProjectedAmount(projected ? numberToMoneyFormat(projected, 0) : "$0")
    setIncomeType(income.incomeTypeDef.label)
    setOwner(model.getPerson(income.ownerId)?.nickname ?? Model.jointNickname)
    setSchedule(income.schedule.getDescription(income.startDate))
    setStartDate(dateToLocalFormat(income.startDate))
    setEndDate(dateToLocalFormat(income.endDate))
    setAnnualInf(numberToPercentFormat(isNumber(income.annualInf) ? income.annualInf : model.getDefaultInflationRate("income"), 2))
    setAnnualInfLock(!isNumber(income.annualInf) || model.hasLock(income.id, "annualInf"))
    if (income.incomeTypeDef.requiredExtraFields && (income.incomeTypeDef.requiredExtraFields.includes("survivorAnnuityPercent"))) {
      setSurvivorPercent(income.survivorPercent !== undefined ? `${income.survivorPercent}%` : undefined)
    }
    if (income.paycheckId) {
      const paycheck = model.getIncomeById(income.paycheckId)
      if (paycheck) {
        setLabel(paycheck.description)
      }
    }
    if (isManagedRiderIncome(model, income)) {
      const linkedAsset = model.getAssetById(income.assetId)
      if (linkedAsset) {
        setLabel(linkedAsset.description)
      }
    }
    setArchived(income.archived)
    setIsLocked(model.hasLock(income.id, "*"))
    setEditOpen(!income.createdAt)
  }

  const theme = useTheme()
  const { notify } = useResources()

  const styles = {
    heading: {
      color: theme.palette.primary.main,
      fontWeight: 'bold'
    },
    row: {
      height: 40,
      minHeight: 40,
      maxHeight: 40,
    },
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "1.0em",
      maxHeight: 15,
      marginBottom: "1px",
      textWrap: "nowrap",
      color: theme.palette.grey["700"]
    },
    value: {
      border: "1px solid red"
    }
  }

  const renderContent = () => {
    return (
      <Grid container direction="column" spacing={0}>
        <Grid item xs={12} sx={styles.row}>
          <Grid container direction="row" spacing={0}>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Amount</FormLabel>
              <Typography variant="body2">
                {amount}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Amount Date</FormLabel>
              <Typography variant="body2">
                {amountDate}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={styles.row}>
          <FormLabel sx={styles.formLabel}>Frequency</FormLabel>
          <Typography variant="body2">
            {schedule}
          </Typography>
        </Grid>
        <Grid item xs={12} sx={styles.row}>
          <Grid container direction="row" spacing={0}>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Owner</FormLabel>
              <Typography variant="body2">
                {owner}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <FormLabel sx={styles.formLabel}>Income Type</FormLabel>
              <Typography variant="body2" style={{height:20, overflowY:"hidden"}}>
                {incomeType}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {survivorPercent !== undefined && survivorPercent !== "0%" &&
          <Grid item xs={12} sx={styles.row}>
            <Grid container direction="row" spacing={0}>
              <Grid item xs={4}>
              </Grid>
              <Grid item xs={8}>
                <FormLabel sx={styles.formLabel}>Survivor %</FormLabel>
                <Typography variant="body2">
                  {survivorPercent}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        }
        <Grid item xs={12} sx={styles.row}>
          <Grid container direction="row" spacing={0}>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Start Date</FormLabel>
              <Typography variant="body2">
                {startDate}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>End Date</FormLabel>
              <Typography variant="body2" color={income.end ? "inherit" : "darkgrey"}>
                {endDate}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Annual Inflation %</FormLabel>
              <Typography variant="body2" color={annualInfLock ? "text.secondary" : "inherit"}>
                {annualInf}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  return (
    <React.Fragment>
    <ModelCard2
      icon={<IncomeIcon/>}
      label={label}
      title={description}
      value={projectedAmount}
      archived={archived}
      expanded={expanded}
      bgColor={isLocked ? theme.palette.grey[100] : undefined}
      onChange={onChange}
      editButtonId={`edit-btn-${incomeModel.id}`}
      onEdit={(event: any) => {
        if (isManagedRiderIncome(model, incomeModel)) {
          // Rider income is managed by its linked annuity asset
          notify.show('info', "This income is managed by its linked asset. Edit it from the Assets & Liabilities page.")
          return
        }
        setEditOpen(!editOpen)
      }}
      dragHandleProps={dragHandleProps}
    >
      {renderContent()}
    </ModelCard2>
      {editOpen && incomeModel.isPaycheck &&
        <PaycheckEditDialog income={incomeModel} model={model} snapshot={snapshot}
                          open={editOpen}
                          onSave={(update: Income) => {
                            init(update)
                            setEditOpen(false)
                            if (onSave) {
                              onSave(update)
                              setSaved(true)
                            }
                          }}
                          onDelete={(income: Income) => {
                            setEditOpen(false)
                            if (onDelete) {
                              onDelete(income)
                            }
                          }}
                          onArchive={(income: Income) => {
                            setEditOpen(false)
                            if (onArchive) {
                              onArchive(income)
                            }
                          }}
                          onClose={() => {
                            setEditOpen(false)
                            if (!incomeModel.createdAt && onDelete) {
                              onDelete(incomeModel)
                            }
                          }}/>
      }
      {editOpen && !incomeModel.isPaycheck &&
        <IncomeEditDialog income={incomeModel} model={model} snapshot={snapshot}
                          open={editOpen}
                          onSave={(update: Income) => {
                            init(update)
                            setEditOpen(false)
                            if (onSave) {
                              onSave(update)
                              setSaved(true)
                            }
                          }}
                          onDelete={(income: Income) => {
                            setEditOpen(false)
                            if (onDelete) {
                              onDelete(income)
                            }
                          }}
                          onArchive={(income: Income) => {
                            setEditOpen(false)
                            if (onArchive) {
                              onArchive(income)
                            }
                          }}
                          onClose={() => {
                            setEditOpen(false)
                            if (!incomeModel.createdAt && onDelete) {
                              onDelete(incomeModel)
                            }
                          }}/>
      }
    </React.Fragment>
  )
}

export default IncomeCard